import { Skeleton } from "@mui/material";
import { useEffect } from "react";
import { updateSegmentationVisibility } from "stores/viewer";
import useMarkers from "shared/hooks/useMarkers";
import { useParams } from "react-router";
import SequenceMarkers from "./SequenceMarkers";
import { useSnapshot } from "valtio";
import viewerState from "stores/viewer";
import { switchNiftiBase } from "lib/cornerstone/setupCornerstoneViewer";

function QuantificationTable() {
  const { study_id } = useParams();
  const { naturalizedDatasets, displaySegment, isLoading, populatedMarkers } =
    useMarkers(study_id);
  const { niftiDatasets, niftiActiveModality } = useSnapshot(viewerState);
  // Switch back to T1 if coming from another modality (e.g. microbleed T2*)
  useEffect(() => {
    if (niftiActiveModality && niftiActiveModality !== "t1mri") {
      // Clear all selected segments (removes T2* overlays)
      viewerState.segmentsPerSeries = viewerState.segmentsPerSeries.map(
        (s) => ({ ...s, selected: false }),
      );
      updateSegmentationVisibility().catch(console.error);
      const t1Url = (viewerState.niftiBases as any)?.t1mri;
      if (t1Url) {
        switchNiftiBase(t1Url).catch(console.error);
        viewerState.niftiActiveModality = "t1mri";
        viewerState.focusedMarkerCode = null;
      }
    }
  }, []);

  if (isLoading) {
    return (
      <div>
        <Skeleton
          className="bg-stone-500 leading-0"
          animation={false}
          height={50}
        />
        <Skeleton className="bg-stone-500" animation="wave" height={50} />
        <Skeleton className="bg-stone-500" animation="wave" height={50} />
        <Skeleton className="bg-stone-500" animation="wave" height={50} />
        <Skeleton className="bg-stone-500" animation="wave" height={50} />
        <Skeleton className="bg-stone-500" animation="wave" height={50} />
        <Skeleton className="bg-stone-500" animation="wave" height={50} />
      </div>
    );
  }

  // nifti mode: only use synthesized datasets, skip DICOM SEG (avoid duplicate rows)
  const isNiftiMode = niftiDatasets.length > 0;
  const dicomSegDatasets = isNiftiMode
    ? []
    : naturalizedDatasets?.filter((d) => d.Modality === "SEG") ?? [];
  const allSegDatasets = [...dicomSegDatasets, ...niftiDatasets].filter(
    (d) => d.SeriesInstanceUID !== "nifti:seg:t2starmri",
  );

  return (
    <table
      data-testid="viewer-mode-quantification"
      className="w-full rounded overflow-x-clip text-right"
    >
      <thead className="bg-stone-600 rounded sticky top-0 z-50 ">
        <tr>
          <th className="py-2 rounded-tl w-8"></th>
          <th className="py-2 text-left w-72">Structure</th>
          <th className="py-2">Volume(mL)</th>
          <th className="py-2">Volume(%ICV)</th>
          <th className="py-2">Zscore</th>
          <th className="py-2">Percentile</th>
          <th className="py-2 rounded-tr w-8"></th>
        </tr>
      </thead>
      <tbody>
        {allSegDatasets.length
          ? allSegDatasets
              .sort((dA, dB) => {
                const orderA = (dA as any)._order ?? Infinity;
                const orderB = (dB as any)._order ?? Infinity;
                if (orderA !== Infinity || orderB !== Infinity) {
                  return orderA - orderB;
                }
                return (dA.SeriesDescription ?? "").localeCompare(
                  dB.SeriesDescription ?? "",
                );
              })
              .map((d) => (
                <SequenceMarkers
                  key={d.SeriesInstanceUID}
                  dataset={d}
                  populatedMarkers={populatedMarkers}
                  displaySegment={displaySegment}
                />
              ))
          : null}
      </tbody>
    </table>
  );
}

export default QuantificationTable;
